import { Coordinate } from "@nova/protobuf"
import { MultiPolygonWithHoles, PolygonWithHoles } from "@nova/constants"
import { pointInPolygon } from "./calculate-geospatial"
import { getPolygonArea } from "./polygon-area"
import { polygonDifference } from "./polygon-clip-greiner-hormann"

export { PolygonDifferenceUnstableError } from "./polygon-clip-greiner-hormann"

// loop[0] is always a boundary-touching intersection point by construction, so it can't be
// used to test nesting. The globally lowest vertex of any simple polygon is always convex,
// so the centroid of it and its two neighbors is guaranteed to sit strictly inside the loop.
function interiorPoint(loop: Coordinate[]) {
    const points = loop.slice(0, -1)
    const lowestIndex = points.reduce(
        (lowest, point, i) => (point.latitude < points[lowest].latitude ? i : lowest),
        0
    )
    const previous = points[(lowestIndex - 1 + points.length) % points.length]
    const current = points[lowestIndex]
    const next = points[(lowestIndex + 1) % points.length]
    return {
        longitude: (previous.longitude + current.longitude + next.longitude) / 3,
        latitude: (previous.latitude + current.latitude + next.latitude) / 3,
    }
}

// Largest-first, testing each loop only against already-accepted (hence bigger) pieces,
// avoids full pairwise nesting checks and still handles nesting deeper than one level.
function groupLoopsIntoPieces(loops: Coordinate[][], existingHoles: Coordinate[][]) {
    const byDescendingArea = [...loops].sort(
        (a, b) => getPolygonArea({ outer: b, holes: [] }) - getPolygonArea({ outer: a, holes: [] })
    )

    const pieces: PolygonWithHoles[] = []
    for (const loop of byDescendingArea) {
        const point = interiorPoint(loop)
        const parent = pieces.find((piece) => pointInPolygon(point, piece.outer))
        if (parent) parent.holes.push(loop)
        else pieces.push({ outer: loop, holes: [] })
    }

    for (const hole of existingHoles) {
        const point = interiorPoint(hole)
        pieces.find((piece) => pointInPolygon(point, piece.outer))?.holes.push(hole)
    }

    return pieces
}

function subtractFromPiece(piece: PolygonWithHoles, hole: PolygonWithHoles) {
    const loops = polygonDifference(piece.outer, hole.outer)
    if (!loops.length) return []
    if (loops.length === 1) return [{ outer: loops[0], holes: piece.holes }]
    return groupLoopsIntoPieces(loops, piece.holes)
}

export function subtractPolygon({
    base,
    hole,
}: {
    base: MultiPolygonWithHoles
    hole: PolygonWithHoles
}) {
    return base.flatMap((piece) => subtractFromPiece(piece, hole))
}
